import React from 'react';
import { Calendar, MapPin, Clock, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import RSVPForm from '../components/RSVPForm';
import { Toaster } from '../components/ui/sonner';

const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-ivory-50 to-white">
      <Toaster position="top-center" richColors />
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1729250036760-6764204a4f80"
            alt="Seamill coastal view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-lightskyblue-50/70 via-ivory-50/80 to-ivory-100/90"></div>
        </div>

        {/* Decorative corner flourishes */}
        <div className="absolute top-8 left-8 w-24 h-24 border-t-2 border-l-2 border-gold-400 opacity-60"></div>
        <div className="absolute top-8 right-8 w-24 h-24 border-t-2 border-r-2 border-gold-400 opacity-60"></div>
        <div className="absolute bottom-8 left-8 w-24 h-24 border-b-2 border-l-2 border-gold-400 opacity-60"></div>
        <div className="absolute bottom-8 right-8 w-24 h-24 border-b-2 border-r-2 border-gold-400 opacity-60"></div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <div className="mb-8 animate-fade-in">
          </div>
          
          <h1 className="font-script text-7xl md:text-9xl mb-6 text-lightskyblue-700 animate-fade-in-up" style={{lineHeight: '1.2'}}>
            Christie & Kieran
          </h1>
          
          <div className="flex items-center justify-center gap-4 mb-6 animate-fade-in-up animation-delay-200">
            <div className="h-px w-16 bg-gold-400"></div>
            <p className="font-serif text-2xl md:text-3xl text-lightskyblue-800 tracking-widest">
              THOMSON · WHITE
            </p>
            <div className="h-px w-16 bg-gold-400"></div>
          </div>

          <div className="flex items-center justify-center gap-3 text-lightskyblue-700 mb-6 animate-fade-in-up animation-delay-400">
            <p className="text-xl md:text-2xl font-body">
              Saturday, 22nd August 2026
            </p>
          </div>

          <div className="flex items-center justify-center gap-3 text-lightskyblue-600 animate-fade-in-up animation-delay-600">
            <p className="text-lg md:text-xl font-body italic">
              Seamill Hydro, West Kilbride, Ayrshire
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold-400 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-gold-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Venue Details Section */}
      <section className="py-20 px-4 bg-ivory-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-gold-400 text-4xl mb-4">✦</div>
            <h2 className="font-serif text-5xl md:text-6xl text-lightskyblue-800 mb-4">
              Join Us
            </h2>
            <p className="text-lg text-lightskyblue-700 max-w-2xl mx-auto font-body italic">
              We can't wait to celebrate our special day with you
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Venue Card */}
            <div className="vintage-card rounded-lg p-8 transform hover:scale-105 transition-transform duration-300">
              <div className="mb-6 relative">
                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-gold-400 rounded-lg -z-10"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_bride-groom-hub-40/artifacts/e5icxcyz_Seamill%20hydro%20cover%20photo.jpg"
                  alt="Seamill Hydro Hotel"
                  className="w-full h-64 object-cover rounded-lg border-4 border-ivory-100"
                />
              </div>
              <h3 className="font-serif text-3xl text-lightskyblue-800 mb-4 text-center">The Venue</h3>
              <div className="w-16 h-1 bg-gold-400 mx-auto mb-6"></div>
              <div className="text-center text-lightskyblue-700 font-body space-y-2">
                <p className="text-xl font-semibold text-lightskyblue-800">Seamill Hydro Hotel</p>
                <p className="text-lg">39 Ardrossan Road</p>
                <p className="text-lg">West Kilbride, Ayrshire</p>
                <p className="text-lg">Scotland, KA23 9ND</p>
              </div>
            </div>

            {/* Google Maps Card */}
            <div className="vintage-card rounded-lg p-8">
              <h3 className="font-serif text-3xl text-lightskyblue-800 mb-4 text-center">Location</h3>
              <div className="w-16 h-1 bg-gold-400 mx-auto mb-6"></div>
              <div className="w-full h-80 bg-lightskyblue-50 rounded-lg border-4 border-ivory-100 overflow-hidden">
                <iframe
                  title="Seamill Hydro Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2235.5!2d-4.867!3d55.683!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4889a0e4c8a8b8b1%3A0x1234567890abcdef!2sSeamill%20Hydro%20Hotel!5e0!3m2!1sen!2suk!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
              <div className="mt-4 text-center">
                <a
                  href="https://maps.google.com/?q=Seamill+Hydro+Hotel+West+Kilbride+KA23+9ND"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lightskyblue-600 hover:text-lightskyblue-800 text-sm font-body underline transition-colors"
                >
                  Open in Google Maps →
                </a>
              </div>
            </div>
          </div>

          {/* Travel Information Section */}
          <div className="mt-12">
            <div className="vintage-card rounded-lg p-8">
              <h3 className="font-serif text-3xl text-lightskyblue-800 mb-4 text-center">Getting Here</h3>
              <div className="w-16 h-1 bg-gold-400 mx-auto mb-8"></div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* From Glasgow Central */}
                <div className="bg-lightskyblue-50 p-6 rounded-lg border-l-4 border-gold-400">
                  <h4 className="font-serif text-xl text-lightskyblue-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">🚂</span>
                    From Glasgow Central Station
                  </h4>
                  <div className="space-y-3 font-body text-lightskyblue-700">
                    <p className="flex items-start gap-2">
                      <span className="text-gold-500 font-bold">1.</span>
                      <span>Board the train heading to <strong>Largs</strong></span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-gold-500 font-bold">2.</span>
                      <span>Get off at <strong>West Kilbride</strong> station</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-gold-500 font-bold">3.</span>
                      <span>The hotel is a short taxi ride or 15-minute walk from the station</span>
                    </p>
                  </div>
                </div>

                {/* From Glasgow Airport */}
                <div className="bg-lightskyblue-50 p-6 rounded-lg border-l-4 border-gold-400">
                  <h4 className="font-serif text-xl text-lightskyblue-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">✈️</span>
                    From Glasgow Airport
                  </h4>
                  <div className="space-y-3 font-body text-lightskyblue-700">
                    <p className="flex items-start gap-2">
                      <span className="text-gold-500 font-bold">1.</span>
                      <span>Take the airport bus to <strong>Paisley Gilmour Street</strong> station</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-gold-500 font-bold">2.</span>
                      <span>Board the train to <strong>Largs</strong></span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-gold-500 font-bold">3.</span>
                      <span>Get off at <strong>West Kilbride</strong> station</span>
                    </p>
                    <p className="flex items-start gap-2">
                      <span className="text-gold-500 font-bold">4.</span>
                      <span>The hotel is a short taxi ride or 15-minute walk from the station</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-ivory-100 rounded-lg border border-gold-400/30">
                <p className="text-sm text-lightskyblue-700 font-body text-center italic">
                  <strong>Journey Time:</strong> Approximately 45-50 minutes from Glasgow Central | 
                  75-90 minutes from Glasgow Airport (including transfers)
                </p>
              </div>

              {/* Taxi Services Section */}
              <div className="mt-8 pt-8 border-t-2 border-gold-400/30">
                <h4 className="font-serif text-2xl text-lightskyblue-800 mb-6 text-center">Local Taxi Services</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-lightskyblue-50 p-4 rounded-lg border-l-4 border-gold-400">
                    <p className="font-semibold text-lightskyblue-800 mb-2 font-serif">Eddies</p>
                    <div className="space-y-1 text-sm text-lightskyblue-700 font-body">
                      <p><a href="tel:+441294822812" className="hover:underline">+44 1294 822812</a></p>
                      <p><a href="tel:+447425628006" className="hover:underline">+44 7425 628006</a></p>
                    </div>
                  </div>

                  <div className="bg-lightskyblue-50 p-4 rounded-lg border-l-4 border-gold-400">
                    <p className="font-semibold text-lightskyblue-800 mb-2 font-serif">Iain (Airport Only)</p>
                    <div className="space-y-1 text-sm text-lightskyblue-700 font-body">
                      <p><a href="tel:+447821568255" className="hover:underline">+44 7821 568255</a></p>
                    </div>
                  </div>

                  <div className="bg-lightskyblue-50 p-4 rounded-lg border-l-4 border-gold-400">
                    <p className="font-semibold text-lightskyblue-800 mb-2 font-serif">Central</p>
                    <div className="space-y-1 text-sm text-lightskyblue-700 font-body">
                      <p><a href="tel:+441294464646" className="hover:underline">+44 1294 464646</a></p>
                    </div>
                  </div>

                  <div className="bg-lightskyblue-50 p-4 rounded-lg border-l-4 border-gold-400">
                    <p className="font-semibold text-lightskyblue-800 mb-2 font-serif">Streamline</p>
                    <div className="space-y-1 text-sm text-lightskyblue-700 font-body">
                      <p><a href="tel:+441294654000" className="hover:underline">+44 1294 654000</a></p>
                    </div>
                  </div>

                  <div className="bg-lightskyblue-50 p-4 rounded-lg border-l-4 border-gold-400 md:col-span-2">
                    <p className="font-semibold text-lightskyblue-800 mb-2 font-serif">Phoenix</p>
                    <div className="space-y-1 text-sm text-lightskyblue-700 font-body">
                      <p><a href="tel:+441294121020" className="hover:underline">+44 1294 121020</a> / <a href="tel:+441294277888" className="hover:underline">+44 1294 277888</a></p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-lightskyblue-600 mt-4 text-center font-body italic">
                  All taxi numbers include international dialing code for guests calling from Ireland
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-gold-400 text-4xl mb-4">✦</div>
            <h2 className="font-serif text-5xl md:text-6xl text-lightskyblue-800 mb-4">
              Wedding Schedule
            </h2>
            <p className="text-lg text-lightskyblue-700 font-body italic">
              Two days of celebration
            </p>
          </div>

          <div className="space-y-8">
            {/* Day 1 - Saturday */}
            <div className="vintage-card rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-lightskyblue-600 to-lightskyblue-500 text-white p-6 border-b-4 border-gold-400">
                <div className="text-center">
                  <h3 className="font-serif text-3xl mb-2">Day One</h3>
                  <p className="text-lightskyblue-50 font-body">Saturday, 22nd August 2026</p>
                </div>
              </div>
              <div className="p-8">
                <div className="space-y-6">
                  <div className="border-b border-lightskyblue-200 pb-6">
                    <p className="font-semibold text-xl text-lightskyblue-800 font-serif mb-2">13:00 - Wedding Ceremony</p>
                    <p className="text-lightskyblue-600 font-body italic">
                      Join us as we exchange our vows and begin our journey together
                    </p>
                  </div>

                  <div className="border-b border-lightskyblue-200 pb-6">
                    <p className="font-semibold text-xl text-lightskyblue-800 font-serif mb-2">Following Ceremony - Wedding Breakfast</p>
                    <p className="text-lightskyblue-600 font-body italic">
                      Enjoy a delicious meal with family and friends
                    </p>
                  </div>

                  <div>
                    <p className="font-semibold text-xl text-lightskyblue-800 font-serif mb-2">Evening - Drinks & Dancing</p>
                    <p className="text-lightskyblue-600 font-body italic">
                      Celebrate with us late into the night with drinks, music, and dancing
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Day 2 - Sunday */}
            <div className="vintage-card rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-lightskyblue-500 to-lightskyblue-400 text-white p-6 border-b-4 border-gold-400">
                <div className="text-center">
                  <h3 className="font-serif text-3xl mb-2">Day Two</h3>
                  <p className="text-lightskyblue-50 font-body">Sunday, 23rd August 2026</p>
                </div>
              </div>
              <div className="p-8">
                <div>
                  <p className="font-semibold text-xl text-lightskyblue-800 font-serif mb-2">16:00 - Casual Celebration</p>
                  <p className="text-lightskyblue-600 font-body mb-4 italic">
                    Join us for a more relaxed day at Seamill Hydro for those who wish to continue the celebrations
                  </p>
                  <div className="bg-lightskyblue-50 p-4 rounded-lg border-l-4 border-gold-400">
                    <p className="text-lightskyblue-700 font-body">
                      <span className="font-semibold">All are welcome!</span> Enjoy laid-back drinks,
                      delicious food, and music in a casual atmosphere.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RSVP Section */}
      <section className="py-20 px-4 bg-ivory-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-gold-400 text-4xl mb-4">✦</div>
            <h2 className="font-serif text-5xl md:text-6xl text-lightskyblue-800 mb-4">
              Will You Join Us?
            </h2>
            <div className="decorative-divider mb-6">
            </div>
            <p className="text-lg text-lightskyblue-700 font-body italic">
              We'd love to have you celebrate with us
            </p>
          </div>

          <RSVPForm />
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-gold-400 text-4xl mb-4">✦</div>
            <h2 className="font-serif text-5xl md:text-6xl text-lightskyblue-800 mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-lightskyblue-700 font-body italic">
              Celebrating our love story
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-4 border-ivory-100">
              <img
                src="https://customer-assets.emergentagent.com/job_bride-groom-hub-40/artifacts/yyukpw5w_Wedding%20web%204.jpg"
                alt="Christie and Kieran tropical swing"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-4 border-ivory-100">
              <img
                src="https://customer-assets.emergentagent.com/job_bride-groom-hub-40/artifacts/0cvkf08z_Wedding%20web%202.jpg"
                alt="Christie and Kieran celebration"
                className="w-full h-80 object-cover"
              />
            </div>
            <div className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border-4 border-ivory-100">
              <img
                src="https://customer-assets.emergentagent.com/job_bride-groom-hub-40/artifacts/q9ghts0x_Wedding%20web%203.jpeg"
                alt="Christie and Kieran together"
                className="w-full h-80 object-cover"
                style={{ filter: 'brightness(1.3) contrast(1.05)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-lightskyblue-800 to-lightskyblue-900 text-white py-12 px-4 border-t-4 border-gold-400">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-script text-4xl mb-2">Christie & Kieran</p>
          <p className="text-lightskyblue-200 mb-6 font-body">22nd August 2026</p>
          <Separator className="bg-gold-400/30 mb-6" />
          <p className="text-lightskyblue-300 text-sm font-body italic">
            We can't wait to celebrate with you at Seamill Hydro
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home;